import { useState, useEffect, useCallback } from 'react';
import axiosInstance, { extractErrorMessage } from '../../../services/axiosConfig';

/**
 * Tipo de atividade do usuário
 */
export type UserActivityType = 'chat' | 'project';

/**
 * Interface para atividade do usuário
 */
export interface Activity {
  id: string;
  type: UserActivityType;
  title: string;
  description: string;
  tokensUsed: number;
  timestamp: string; // ISO 8601 date string
  relativeTime?: string; // "há 15 minutos", "há 8 horas", etc.
}

/**
 * Hook para buscar atividades recentes do usuário
 *
 * Busca últimas interações (chats e projetos) com tokens gastos
 *
 * @param limit - Número de atividades a buscar (default: 4)
 * @returns Estado e funções para gerenciar atividades
 *
 * @example
 * ```tsx
 * const { activities, loading, error } = useRecentActivity(4);
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage>{error}</ErrorMessage>;
 *
 * return activities.map(activity => <ActivityItem key={activity.id} {...activity} />);
 * ```
 */
export const useRecentActivity = (limit: number = 4) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecentActivity = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(`/users/activities/recent?limit=${limit}`);

      // Extract data from BaseResponse structure
      if (response.data?.success && response.data?.data?.activities) {
        setActivities(response.data.data.activities);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Não foi possível carregar as atividades recentes'
      );
      setError(errorMessage);
      console.error('Error fetching recent activities:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecentActivity();
  }, [fetchRecentActivity]);

  return {
    activities,
    loading,
    error,
    refetch: fetchRecentActivity,
  };
};
